import * as THREE from 'three';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { LabelMesh } from './LabelMesh';
import { POI } from '../poi/POI';
import { Scene } from '../core/Scene';
import { LabelMeshType } from '../types';

/**
 * 📍 Screen Position Options - Clock-based positioning
 */
export enum ScreenPosition {
    TOP_CENTER = 'top-center',           // 12 o'clock
    TOP_RIGHT = 'top-right',             // 1:30 o'clock
    RIGHT_CENTER = 'right-center',       // 3 o'clock
    BOTTOM_RIGHT = 'bottom-right',       // 4:30 o'clock
    BOTTOM_CENTER = 'bottom-center',     // 6 o'clock
    BOTTOM_LEFT = 'bottom-left',         // 7:30 o'clock
    LEFT_CENTER = 'left-center',         // 9 o'clock
    TOP_LEFT = 'top-left',               // 10:30 o'clock
    CENTER = 'center'                    // Dead center (but you said this is dumb 😄)
}

/**
 * 📌 ScreenHTMLStaticLabelMesh - Fixed Screen Position HTML Labels
 * 
 * Unlike ScreenHTMLLabelMesh (which syncs with 3D world space), this creates:
 * - Pure HTML elements at FIXED screen positions
 * - No world space tracking or camera projection
 * - User picks position like "12 o'clock", "3 o'clock", etc.
 * - Perfect for UI overlays, notifications, status indicators
 * 
 * Think of it as HTML widgets that stay put on screen!
 */
export class ScreenHTMLStaticLabelMesh extends LabelMesh {
    private _element: HTMLElement | null = null;
    private _fadeAnimation: Animation | null = null;
    private _isVisible: boolean = false;
    private _screenPosition: ScreenPosition = ScreenPosition.TOP_RIGHT;
    
    // 🎯 Fixed width to prevent layout shifts
    private _calculatedWidth: number | null = null;
    
    // Global container for all static screen labels
    private static _globalContainer: HTMLElement | null = null;

    constructor(poi: POI, screenPosition: ScreenPosition = ScreenPosition.TOP_RIGHT) {
        super(poi, 'ScreenHTMLStaticLabelMesh');
        
        this._screenPosition = screenPosition;
        this.ensureGlobalContainer();
    }

    /**
     * Ensure global container exists for all static screen HTML labels
     */
    private ensureGlobalContainer(): void {
        if (!ScreenHTMLStaticLabelMesh._globalContainer) {
            ScreenHTMLStaticLabelMesh._globalContainer = document.createElement('div');
            ScreenHTMLStaticLabelMesh._globalContainer.id = 'screen-html-static-labels-container';
            ScreenHTMLStaticLabelMesh._globalContainer.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                pointer-events: none;
                z-index: 300;
                overflow: hidden;
            `;
            document.body.appendChild(ScreenHTMLStaticLabelMesh._globalContainer);
            console.log('📌 Created global static screen HTML labels container');
        }
    }

    /**
     * Initialize the static screen-space HTML element
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) return;

        try {
            // Create the HTML element
            this._element = document.createElement('div');
            this._element.className = 'screen-html-static-label';
            this._element.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 12px 16px;
                border-radius: 8px;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 14px;
                line-height: 1.4;
                max-width: 300px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                backdrop-filter: blur(8px);
                opacity: 0;
                transform: translateZ(0);
                transition: opacity 0.3s ease-in-out;
                pointer-events: auto;
                word-wrap: break-word;
                z-index: 301;
            `;

            // Add to global container
            if (ScreenHTMLStaticLabelMesh._globalContainer) {
                ScreenHTMLStaticLabelMesh._globalContainer.appendChild(this._element);
            }
            
            // 🎯 Configure pointer events based on labelInteractive setting
            this.updatePointerEvents();

            // Set initial content
            const content = this.poi.poidata.html?.value || this.poi.name || 'Static HTML Label';
            this.updateContent(content);

            // Apply style
            this.updateStyle();
            
            // 🎯 Listen to labelInteractive property changes
            this.poi.poidata.labelInteractive.on(() => {
                this.updatePointerEvents();
            });
            
            // 🎯 Calculate and fix optimal width
            this.calculateOptimalWidth();

            // 📍 Set static position
            this.updateStaticPosition();

            // Create invisible 3D mesh (for compatibility)
            this.createInvisible3DMesh();

            this._isInitialized = true;
            //console.log(`✅ ScreenHTMLStaticLabelMesh initialized for ${this.poi.name} at ${this._screenPosition}`);

        } catch (error) {
            console.error(`❌ Error initializing ScreenHTMLStaticLabelMesh for ${this.poi.name}:`, error);
        }
    }

    /**
     * Create an invisible 3D mesh for compatibility with the base system
     */
    private createInvisible3DMesh(): void {
        // Create minimal invisible geometry for system compatibility
        this._geometry = new THREE.PlaneGeometry(0.01, 0.01);
        
        // Create invisible material
        const material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            opacity: 0,
            visible: false 
        });
        
        this._mesh = new THREE.Mesh(this._geometry, material);
        this._mesh.name = `ScreenHTMLStaticLabel_${this.poi.name}`;
        
        // Add to 3D scene (invisible, for system compatibility)
        if (Scene.Instance) {
            Scene.Instance.add(this._mesh);
        }
    }

    /**
     * 📍 Update static position based on ScreenPosition enum
     */
    private updateStaticPosition(): void {
        if (!this._element) return;

        const margin = 150; // Distance from screen edges

        // Reset transform to measure properly
        this._element.style.transform = 'none';
        
        // Get element dimensions
        const width = this._element.offsetWidth;
        const height = this._element.offsetHeight;

        let left: string, top: string, transform: string;

        switch (this._screenPosition) {
            case ScreenPosition.TOP_CENTER: // 12 o'clock
                left = '50%';
                top = `${margin}px`;
                transform = 'translateX(-50%)';
                break;

            case ScreenPosition.TOP_RIGHT: // 1:30 o'clock
                left = `calc(100% - ${width + margin}px)`;
                top = `${margin}px`;
                transform = 'none';
                break;

            case ScreenPosition.RIGHT_CENTER: // 3 o'clock
                left = `calc(100% - ${width + margin}px)`;
                top = '50%';
                transform = 'translateY(-50%)';
                break;

            case ScreenPosition.BOTTOM_RIGHT: // 4:30 o'clock
                left = `calc(100% - ${width + margin}px)`;
                top = `calc(100% - ${height + margin}px)`;
                transform = 'none';
                break;

            case ScreenPosition.BOTTOM_CENTER: // 6 o'clock
                left = '50%';
                top = `calc(100% - ${height + margin}px)`;
                transform = 'translateX(-50%)';
                break;

            case ScreenPosition.BOTTOM_LEFT: // 7:30 o'clock
                left = `${margin}px`;
                top = `calc(100% - ${height + margin}px)`;
                transform = 'none';
                break;

            case ScreenPosition.LEFT_CENTER: // 9 o'clock
                left = `${margin}px`;
                top = '50%';
                transform = 'translateY(-50%)';
                break;

            case ScreenPosition.TOP_LEFT: // 10:30 o'clock
                left = `${margin}px`;
                top = `${margin}px`;
                transform = 'none';
                break;

            case ScreenPosition.CENTER: // Dead center (the "dumb" one 😄)
                left = '50%';
                top = '50%';
                transform = 'translate(-50%, -50%)';
                break;

            default:
                // Fallback to top-right
                left = `calc(100% - ${width + margin}px)`;
                top = `${margin}px`;
                transform = 'none';
        }

        this._element.style.left = left;
        this._element.style.top = top;
        this._element.style.transform = transform;

        //console.log(`📍 Static label positioned at ${this._screenPosition}: ${left}, ${top}`);
    }

    /**
     * 🎯 Change the static position
     */
    public setScreenPosition(position: ScreenPosition): void {
        this._screenPosition = position;
        this.updateStaticPosition();
        //console.log(`📍 Label ${this.poi.name} moved to ${position}`);
    }

    /**
     * 🎯 Calculate optimal width based on content and fix it
     */
    private calculateOptimalWidth(): void {
        if (!this._element) return;

        // Temporarily position off-screen to measure
        const originalLeft = this._element.style.left;
        const originalTop = this._element.style.top;
        const originalMaxWidth = this._element.style.maxWidth;
        
        this._element.style.left = '-9999px';
        this._element.style.top = '-9999px';
        this._element.style.maxWidth = 'none';
        this._element.style.width = 'auto';
        this._element.style.opacity = '1';
        
        // Force layout
        this._element.offsetHeight;
        
        // Measure natural width
        const naturalWidth = this._element.offsetWidth;
        
        // Calculate optimal width (capped at reasonable limits)
        const maxAllowedWidth = Math.min(window.innerWidth * 0.3, 350); // 30% of screen or 350px max
        const minAllowedWidth = 120;
        
        this._calculatedWidth = Math.max(
            minAllowedWidth,
            Math.min(naturalWidth, maxAllowedWidth)
        );
        
        // Apply fixed width
        this._element.style.width = `${this._calculatedWidth}px`;
        this._element.style.maxWidth = `${this._calculatedWidth}px`;
        this._element.style.minWidth = `${this._calculatedWidth}px`;
        
        // Restore original position
        this._element.style.left = originalLeft;
        this._element.style.top = originalTop;
        this._element.style.opacity = '0';
        
        //console.log(`🎯 Calculated fixed width for static label ${this.poi.name}: ${this._calculatedWidth}px`);
    }

    /**
     * Update content with Markdown support
     */
    updateContent(text: string): void {
        if (!this._element) return;

        try {
            // Parse Markdown to HTML
            const htmlContent = marked.parse(text) as string;
            
            // Sanitize HTML
            const cleanHtml = DOMPurify.sanitize(htmlContent, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre'],
                ALLOWED_ATTR: []
            });

            this._element.innerHTML = cleanHtml;
            
            // 🎯 Recalculate width and position after content change
            this.calculateOptimalWidth();
            this.updateStaticPosition();
            
        } catch (error) {
            console.error('Error updating ScreenHTMLStatic content:', error);
            this._element.textContent = text;
            this.calculateOptimalWidth();
            this.updateStaticPosition();
        }
    }

    /**
     * 🎯 Configure pointer events based on labelInteractive setting
     */
    private updatePointerEvents(): void {
        if (!this._element) return;
        
        const isInteractive = this.poi.poidata.labelInteractive.value ?? false;
        
        if (isInteractive) {
            this._element.style.pointerEvents = 'auto';
            this._element.style.cursor = 'pointer';
            //console.log(`🖱️ Static ScreenHTMLLabel ${this.poi.name}: INTERACTIVE mode`);
        } else {
            this._element.style.pointerEvents = 'none';
            this._element.style.cursor = 'default';
            //console.log(`👻 Static ScreenHTMLLabel ${this.poi.name}: NON-INTERACTIVE mode`);
        }
    }

    /**
     * Update label style
     */
    updateStyle(): void {
        if (!this._element) return;

        // Apply POI-specific styling
        const backgroundColor = this.poi.poidata.labelBackgroundColor?.value;
        const backgroundGradient = this.poi.poidata.labelBackgroundGradient.value;
        const textColor = this.poi.poidata.labelColor?.value;
        const fontSize = this.poi.poidata.labelFontSize?.value;

        // Apply background: gradient takes priority over solid color
        if (backgroundGradient && backgroundGradient.trim()) {
            this._element.style.background = backgroundGradient;
            this._element.style.backgroundColor = '';
        } else if (backgroundColor) {
            this._element.style.backgroundColor = backgroundColor.toString();
            this._element.style.background = '';
        }

        if (textColor) {
            this._element.style.color = textColor.toString();
        }

        if (fontSize) {
            this._element.style.fontSize = `${fontSize}px`;
        }

        // Apply background mode
        const backgroundMode = this.poi.poidata.labelBackgroundMode?.value;
        if (backgroundMode === null) {
            this._element.style.backgroundColor = 'transparent';
            this._element.style.backdropFilter = 'none';
            this._element.style.boxShadow = 'none';
        }
        
        this.updatePointerEvents();
    }

    /**
     * 🎭 Show label with fade-in animation
     */
    public show(): void {
        if (!this._element || this._isVisible) return;

        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
        }

        this._isVisible = true;
        
        this._fadeAnimation = this._element.animate([
            { opacity: 0 },
            { opacity: 1 }
        ], {
            duration: 300,
            easing: 'ease-out',
            fill: 'forwards'
        });

        //console.log(`🎭 Static ScreenHTML label shown: ${this.poi.name} at ${this._screenPosition}`);
    }

    /**
     * 🎭 Hide label with fade-out animation
     */
    public hide(): void {
        if (!this._element || !this._isVisible) return;

        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
        }

        this._isVisible = false;

        this._fadeAnimation = this._element.animate([
            { opacity: 1 },
            { opacity: 0 }
        ], {
            duration: 250,
            easing: 'ease-in',
            fill: 'forwards'
        });

        //console.log(`🎭 Static ScreenHTML label hidden: ${this.poi.name}`);
    }

    /**
     * Dispose of the static screen HTML label
     */
    dispose(): void {
        // Cancel any running animation
        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
            this._fadeAnimation = null;
        }

        // Remove HTML element
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
        }
        this._element = null;

        // Remove from 3D scene
        if (this._mesh && Scene.Instance) {
            Scene.Instance.remove(this._mesh);
        }

        // Dispose geometry and mesh
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        this._mesh = null;

        this._isInitialized = false;
        this._isVisible = false;

        //console.log(`🗑️ ScreenHTMLStaticLabelMesh disposed for ${this.poi.name}`);
    }

    /**
     * Override getAspectRatio for static labels
     */
    public getAspectRatio(): number {
        return 1.0;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    getRenderType(): LabelMeshType {
        return LabelMeshType.SCREEN_HTML_STATIC;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected getCanvasWidth(): number {
        return this._element ? this._element.offsetWidth || 300 : 300;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected getCanvasHeight(): number {
        return this._element ? this._element.offsetHeight || 200 : 200;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected applyOpacityToMaterial(opacity: number): void {
        if (this._element) {
            this._element.style.opacity = opacity.toString();
        }
    }

    /**
     * Static cleanup method
     */
    public static dispose(): void {
        if (ScreenHTMLStaticLabelMesh._globalContainer && ScreenHTMLStaticLabelMesh._globalContainer.parentNode) {
            ScreenHTMLStaticLabelMesh._globalContainer.parentNode.removeChild(ScreenHTMLStaticLabelMesh._globalContainer);
            ScreenHTMLStaticLabelMesh._globalContainer = null;
        }
    }

    /**
     * 📍 Get current screen position
     */
    public getScreenPosition(): ScreenPosition {
        return this._screenPosition;
    }

    /**
     * 🎯 Static method to get all available positions
     */
    public static getAvailablePositions(): ScreenPosition[] {
        return Object.values(ScreenPosition);
    }
}